import React, { useEffect, useState, useContext } from 'react';
import { ConfigContext } from "../../App";
import { useHistory } from "react-router-dom";
import { createBrowserHistory } from 'history';
import parse from 'html-react-parser';
import { SubscriptionService } from '../../models/subscription-service';
import { EventService } from '../../models/event-service';
import EventTile from '../eventtile/eventtile';
import { ServiceRegistry } from '../../models/service-registry';
import { BsoMapping } from '../../models/mapping-service';
import Spinner from '../spinner/spinner';
import ProductTile from '../producttile/producttile';
import PNSHeader from '../pnsheader/pnsheader';
import { IMS_EVENT, OMEGA_ENV, SLACK_RELOAD_TIME, TEAMS_CONFIG } from '../../constants';
import OMEGA from '../../omega';
import { clearPageState, HideFooter } from '../../utils';
import { SlackStatus, TeamsStatus } from '../../proptype';

export default function ManageSubscription() {

    useEffect(() => {
        document.title = 'Manage Subscriptions - Adobe System Status';
    }, []);

    const eventDefaultValue = {
        element: "Manage Subscription",
        action: "click",
        type: "Component",
        widget: {
            name: "managesubscription",
            type: "component"
        }
    }

    function omega_capture(element: any, widget_name: any, widget_type: any) {
        OMEGA.init().updateAndTrack("event", [{
            ...eventDefaultValue,
            element: element,
            widget: {
                name: widget_name,
                type: widget_type
            },
        }]);
    }

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [subscribedEvents, setSubscribedEvents] = useState([]);
    const [selfSubscriptions, setSelfSubscriptions] = useState([]);
    const [slackSubscriptions, setSlackSubscriptions] = useState<any>({});
    const [teamsSubscriptions, setTeamsSubscriptions] = useState<any>({});
    const [userLanguage, setUserLanguage] = useState('');
    const [email, setEmail] = useState('');
    const [currentTab, setCurrentTab] = useState('product');
    const [langInfo, setLangInfo] = useState(false);
    const [slackInfo, setSlackInfo] = useState(false);
    const [teamsInfo, setTeamsInfo] = useState(false);
    const [timeZoneInfo, setTimeZoneInfo] = useState(false);
    const [timezone, setTimeZone] = useState();
    const [slackStatus, setSlackStatus] = useState(-1);
    const [teamsStatus, setTeamsStatus] = useState(-1);
    const [mounted, setMounted] = useState(true);
    const [bsotogrpid, setBsoToGrpId] = useState({});
    const [slackChannelName, setSlackChannelName] = useState('');
    const [teamsChannelName, setTeamsChannelName] = useState('');
    const locales = useContext(ConfigContext).appConfig.languageData;
    const historyAPI = useHistory();

    const eventService: EventService = EventService.init();
    const subscriptionService: SubscriptionService = SubscriptionService.init();
    subscriptionService.setLocales(locales);
    const serviceRegistry: ServiceRegistry = ServiceRegistry.init();
    const bsomapping: BsoMapping = BsoMapping.init();

    useEffect(() => {
        let slackIntervalId: any = null, slackTimer: any = 0;
        let teamsIntervalId: any = null, teamsTimer: any = 0;
        let teamsRefreshInterval: any = null;
        let previousTeamsStatusRef = { current: teamsStatus };
        
        // Slack window cleanup
        const clearSlackRefresh = () => {
            window.slackWindow = null;
            if (slackIntervalId) {
                clearInterval(slackIntervalId);
            }
            slackIntervalId = null;
        }

        // Teams window cleanup  
        const clearTeamsRefresh = () => {
            window.teamsWindow = null;
            if (teamsIntervalId) {
                clearInterval(teamsIntervalId);
            }
            teamsIntervalId = null;
        }

        // Teams auto-refresh timer
        const startTeamsAutoRefresh = () => {
            if (teamsRefreshInterval) {
                clearInterval(teamsRefreshInterval);
            }
            
            // Store initial status
            previousTeamsStatusRef.current = teamsStatus;
            
            teamsRefreshInterval = setInterval(() => {
                const prevStatus = previousTeamsStatusRef.current;
                getSubscriptionData();
                
                // Check if Teams status changed after refresh
                setTimeout(() => {
                    if (teamsStatus !== prevStatus && teamsStatus !== -1) {
                        //Teams status changed, stopping auto-refresh
                        stopTeamsAutoRefresh();
                        clearTeamsRefresh();
                    } else {
                        previousTeamsStatusRef.current = teamsStatus;
                    }
                }, 1000);
            }, TEAMS_CONFIG.REFRESH_TIME);
        };

        const stopTeamsAutoRefresh = () => {
            if (teamsRefreshInterval) {
                clearInterval(teamsRefreshInterval);
                teamsRefreshInterval = null;
            }
        };

        window.scrollTo(0, 0);
        setLoading(true);
        if (window.adobeIMS) {
            getSubscriptionData();
        } else {
            document.addEventListener(IMS_EVENT, getSubscriptionData);
        }

        // Monitor Slack window (existing functionality - unchanged)
        if (window.slackWindow) {
            slackIntervalId = setInterval(() => {
                if (window.slackWindow) {
                    try {
                        let slackPathName: string = window.slackWindow?.location.pathname;

                        if (slackPathName?.includes('slack/success')) {
                            clearSlackRefresh();
                            window.location.reload();
                        }

                    } catch (e: any) {
                        console.error('Error in managesubscription slack window access:', e);
                        omega_capture(
                            `Error in managesubscription slack window access: ${e.message || e.toString()}`,
                            "managesubscription",
                            "error"
                        );
                    }
                } else {
                    clearSlackRefresh();
                }

                slackTimer += 200;
                if (slackTimer > SLACK_RELOAD_TIME) {
                    clearSlackRefresh();
                }

            }, 200);
        }

        // Monitor Teams window and start timer-based refresh
        if (TEAMS_CONFIG.TEAMS_ENABLED && window.teamsWindow) {
            // Start timer-based auto-refresh when Teams window opens
            startTeamsAutoRefresh();
            
            // Monitor Teams window for closure (separate from refresh timer)
            teamsIntervalId = setInterval(() => {
                if (!window.teamsWindow)  {
                    // Teams window closed - stop refresh
                    clearTeamsRefresh();
                    stopTeamsAutoRefresh();
                }

                teamsTimer += 200;
                if (teamsTimer > TEAMS_CONFIG.TEAMS_REFRESH_TIMEOUT) { // 10 minutes timeout
                    clearTeamsRefresh();
                    stopTeamsAutoRefresh();
                }

            }, 200);
        }

        // if (window.removeSlackWindow) {
        //     intervalId = setInterval(() => {
        //         if (window.removeSlackWindow) {
        //             subscriptionService.getSubscriptions("product").then((result: any) => {

        //                 let selfSubscriptionData = result.data;
        //                 if (selfSubscriptionData.slackStatus === SlackStatus.Revoked) {
        //                     clearRefresh();
        //                     window.location.reload();
        //                 }
        //             });
        //         } else {
        //             clearRefresh();
        //         }

        //         timer += 5000;
        //         if (timer > SLACK_RELOAD_TIME) {
        //             clearRefresh();
        //         }
        //     }, 5000)
        // }
        // Clean up any existing intervals if no active windows
        if (!window.slackWindow && slackIntervalId) {
            clearSlackRefresh();
        }
        if (!window.teamsWindow && teamsIntervalId) {
            clearTeamsRefresh();
            stopTeamsAutoRefresh();
        }

        return () => {
            setMounted(false);
            clearSlackRefresh();
            clearTeamsRefresh();
            stopTeamsAutoRefresh();
            document.removeEventListener(IMS_EVENT, getSubscriptionData);
        }
    }, []);


    function getSubscriptionData() {

        if (!window.adobeIMS?.isSignedInUser()) {
            window.adobeid.redirect_uri = window.location.href;
            window.adobeIMS.signIn();
            return;
        }

        OMEGA.init().updateAndTrack('page', {
            hierarchy: "home:manage_subscription",
            name: "Manage Subscription - Adobe status",
            url: encodeURIComponent(window.location.href),
            type: "pns",
            env: OMEGA_ENV
        });

        if (window.location.hash === '#event') {
            setCurrentTab('event');
        }

        let state: any = historyAPI.location.state;
        let safe: boolean = !!state?.page;
        clearPageState(createBrowserHistory());
        
        if (state?.slackChannelName) {
            setSlackChannelName(state.slackChannelName);
        }
        
        const [
            productPromise,
            servicePromise,
            subscriptionPromise,
            selfSubscriptionPromise,
            slackSubscriptionPromise,
            teamsSubscriptionPromise,
            bsoPromise
        ]: any = [
                eventService.fetch(),
                serviceRegistry.fetch(),
                subscriptionService.getSubscriptions("event", safe),
                subscriptionService.getSubscriptions("product", safe),
                subscriptionService.getSlackSubscription(),
                subscriptionService.getTeamsConversations(),
                bsomapping.fetch()
            ];

        Promise.all([subscriptionPromise, selfSubscriptionPromise, slackSubscriptionPromise, teamsSubscriptionPromise, productPromise, servicePromise,bsoPromise])
            .then((result: any) => [result[0].data, result[1].data, result[2].data,result[3].data,result[4].data,result[5].data, result[6].data])
            .then((result: any) => {
                if (mounted) {
                    let mapping = result[6];
                    setBsoToGrpId(result[6]);
                    let [subscriptionData, selfSubscriptionData, slackSubscriptionData, teamsSubscriptionData]: any = result;

                    if (slackSubscriptionData?.channel && slackSubscriptionData?.active) {
                        setSlackSubscriptions(slackSubscriptionData);
                    }

                    if(teamsSubscriptionData) {
                        setTeamsSubscriptions(teamsSubscriptionData);
                    }

                    setUserLanguage(subscriptionService.getUserDetail().userLanguage);
                    setEmail(subscriptionService.getUserDetail().email);

                    let currentSubs = eventService.getSubscribedOngoingIncidents(subscriptionData);
                    setSubscribedEvents(currentSubs || []);
                    let timeZoneString: any = `${selfSubscriptionData.timeZone} (${selfSubscriptionData.timezoneOffset})`;
                    setTimeZone(timeZoneString);
                    setSlackStatus(selfSubscriptionData.slackStatus);
                    setTeamsStatus(selfSubscriptionData.teamsStatus);

                    let selfSubs = serviceRegistry.mergeOfferingsIntoProduct(selfSubscriptionData ,mapping ) || [];

                    setSelfSubscriptions(selfSubs.sort((a: any, b: any) => a.product.name.localeCompare(b.product.name)));
                    let currentState: any = historyAPI.location.state;

                    if (!selfSubs?.length && currentSubs?.length) {

                        if (historyAPI.location && historyAPI.location.state) {
                            if (currentState?.tab?.length) {
                                setCurrentTab(currentState.tab);
                            }
                        }
                        else {
                            setCurrentTab('event');
                        }
                    } else {
                        if (currentState?.tab?.length) {
                            setCurrentTab(currentState.tab);
                        }
                    }

                    setLoading(false);
                }
            })
            .catch((error: any) => {
                console.error('Error in managesubscription service promises:', error);
                omega_capture(
                    `Error in managesubscription service promises: ${error.message || error.toString()}`,
                    "managesubscription",
                    "error"
                );
                setLoading(false);
                setError(true);
            });
    }

    function getEventTiles() {
        return (
            subscribedEvents.map((incident: any, index: any) => {
                return (
                    <EventTile event={incident} key={index} isHistory={eventService.isCommunication(incident.eventType) ? true : false} isSubscribed={true} isPns={true} />
                );
            })
        );
    }

    function getProductTiles() {
        return (
            <>
                <div>
                    {selfSubscriptions.map((subscription: any) => {
                        return (
                            <ProductTile subscription={subscription} key={subscription.product.id} showAll={true} bsomapping={bsotogrpid} />
                        )
                    })
                    }
                </div>
            </>)
    }


    function switchTab(tab: any) {
        if (tab !== currentTab) {
            window.location.href = '#' + tab;
            setCurrentTab(tab);
            return true;
        }
        return false;
    }

    function renderSlack() {
        let slackText = '', slackInfoText = '';

        if (slackStatus === SlackStatus.Installed && slackSubscriptions.channel) {
            slackText = slackSubscriptions.channel;
            slackInfoText = locales.manage_subscription_slack_channel_icon_info;

        }
        if (slackStatus === SlackStatus.InstallPending) {
            const channelName = slackChannelName || slackSubscriptions.channel;
            slackText = `${locales.slack_setup_pending}${channelName ? ` (#${channelName})` : ''}`;
            slackInfoText = locales.manage_slack_setup_pending_info;

        }

        if (slackStatus === SlackStatus.RevokePending) {
            slackText = locales.slack_removal;
            slackInfoText = locales.manage_slack_removal_info;
        }

        if (slackText && slackInfoText && slackSubscriptions.workSpaceUrl) {
            var workspaceUrl = new URL(slackSubscriptions.workSpaceUrl)
            return (
                <>
                    <div> <i className="prof-icon slack-icon"></i> {locales.slack}: {slackText.startsWith('@') ? <b>{slackText}</b> : <a href={workspaceUrl.origin + '/channels/' + (slackText.startsWith('#') ? slackText.slice(1) : slackText)} rel="noreferrer" target="_blank" className="slack-channel-link"><b>{slackText.startsWith('#') ? slackText : `#${slackText}`}</b></a>}<button 
                        onClick={() => { setSlackInfo(!slackInfo); }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSlackInfo(!slackInfo);
                            }
                        }}
                        className="info-icon"
                        role="button"
                        aria-label={`${slackInfo ? 'Hide' : 'Show'} Slack channel information`}
                        aria-expanded={slackInfo}
                    /></div>                    {slackInfo ? <div className="slack-info">{parse(slackInfoText)}</div> : ""}
                </>
            );
        } else if (slackText && slackInfoText) {
            return (
                <div> <i className="prof-icon slack-icon"></i> {locales.slack}: <b>{slackText}</b><button 
                    onClick={() => { setSlackInfo(!slackInfo); }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSlackInfo(!slackInfo);
                        }
                    }}
                    className="info-icon"
                    role="button"
                    aria-label={`${slackInfo ? 'Hide' : 'Show'} Slack information`}
                    aria-expanded={slackInfo}
                />{slackInfo ? <div className="slack-info">{parse(slackInfoText)}</div> : ""}</div>
            );
        } else {
            return <></>;
        }
    }

    function renderTeams() {
        let teamsText = '', teamsInfoText = '';

        if (teamsStatus === TeamsStatus.Installed) {
            if (teamsSubscriptions.length > 0) {
                teamsText = teamsSubscriptions[0].conversationName;
            }
            teamsInfoText = locales.manage_subscription_teams_channel_icon_info;
        }
        if (teamsStatus === TeamsStatus.InstallPending) {
            const channelName = teamsChannelName || teamsSubscriptions.conversationName;
            teamsText = `${locales.teams_setup_pending}${channelName ? ` (#${channelName})` : ''}`;
            teamsInfoText = locales.manage_subscription_teams_setup_pending_info;
        }

        if (teamsStatus === TeamsStatus.RevokePending) {
            teamsText = locales.teams_removal;
            teamsInfoText = locales.manage_subscription_teams_removal_info;
        }

        if (teamsStatus === TeamsStatus.Unapproved) {
            teamsText = locales.teams_setup_pending;
            teamsInfoText = locales.manage_subscription_teams_unapproved_info;
        }

        if (teamsText && teamsInfoText) {
            return (
                <>
                    <div> <i className="prof-icon teams-icon"></i> {locales.teams}: <b>{teamsText}</b>
                        <button 
                            onClick={() => { setTeamsInfo(!teamsInfo); }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setTeamsInfo(!teamsInfo);
                                }
                            }}
                            className="info-icon"
                            role="button"
                            aria-label={`${teamsInfo ? 'Hide' : 'Show'} Teams information`}
                            aria-expanded={teamsInfo}
                        />
                    </div>
                    {teamsInfo ? <div className="teams-info">{parse(teamsInfoText)}</div> : ""}
                </>
            );
        } else {
            return <></>;
        }
    }

    function printEventInfo() {
        if (loading) {
            return <>{HideFooter(true)}<Spinner /></>;
        } else {
            HideFooter(false)
            if (subscribedEvents.length) {
                return (
                    <div className="tab-content event-content active ">
                        <div className="event-sub-container">
                            <div className="preference">
                                <div><i className="prof-icon email-icon"></i>{locales.sb_email}: <b>{email}</b></div>
                                {renderSlack()}
                                {TEAMS_CONFIG.TEAMS_ENABLED ? renderTeams() : <></>}
                                <div><i className="prof-icon lang-icon"></i>{locales.sb_lang}: <b>{userLanguage}</b><button 
                                    onClick={() => { setLangInfo(!langInfo); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setLangInfo(!langInfo);
                                        }
                                    }}
                                    className="info-icon"
                                    role="button"
                                    aria-label={`${langInfo ? 'Hide' : 'Show'} language information`}
                                    aria-expanded={langInfo}
                                /></div>
                                {langInfo ? <div className="lang-info">{parse(locales.lang_info)}</div> : ""}
                                <div><i className="prof-icon timezone-icon"></i>{locales.timezone}: <b>{timezone}</b><button 
                                    onClick={() => { setTimeZoneInfo(!timeZoneInfo); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setTimeZoneInfo(!timeZoneInfo);
                                        }
                                    }}
                                    className="info-icon"
                                    role="button"
                                    aria-label={`${timeZoneInfo ? 'Hide' : 'Show'} timezone information`}
                                    aria-expanded={timeZoneInfo}
                                /></div>
                                {timeZoneInfo ? <div className="lang-info">{parse(locales.timezone_info)}</div> : ""}
                            </div>
                            <div>{getEventTiles()}</div>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className="text-container no-subscription">
                        <div>{locales.no_subscription}</div>
                        <p>{parse(locales.no_event_susbscription_message)}</p>
                    </div>
                )
            }
        }
    }

    function printProductInfo() {
        if (loading) {
            return <>{HideFooter(true)}<Spinner /></>;
        } else {
            HideFooter(false)
            if (selfSubscriptions.length) {
                return (
                    <div className="animate__animated animate__fadeIn">
                        <div className="pull-right action-btn">
                            <button 
                                className="btn unsubscription" 
                                onClick={() => { historyAPI.push("/proactive-notifications/product/unsubscribe-all") }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        historyAPI.push("/proactive-notifications/product/unsubscribe-all");
                                    }
                                }}
                                aria-label={`${locales.unsubscribe_all}, remove all product subscriptions`}
                                tabIndex={0}
                            >
                                {locales.unsubscribe_all}
                            </button>
                            <button 
                                className="btn edit-subscription" 
                                onClick={() => { historyAPI.push("/proactive-notifications/subscriptions/edit") }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        historyAPI.push("/proactive-notifications/subscriptions/edit");
                                    }
                                }}
                                aria-label={`${locales.edit_subscriptions}, modify subscription preferences`}
                                tabIndex={0}
                            >
                                {locales.edit_subscriptions}
                            </button>
                        </div>
                        <div className="preference">
                            <div> <i className="prof-icon email-icon"></i> {locales.sb_email}: <b>{email}</b></div>
                            {renderSlack()}
                            {TEAMS_CONFIG.TEAMS_ENABLED ? renderTeams() : <></>}
                            <div> <i className="prof-icon lang-icon"></i> {locales.sb_lang}: <b>{userLanguage}</b><button 
                                onClick={() => { setLangInfo(!langInfo); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setLangInfo(!langInfo);
                                    }
                                }}
                                className="info-icon"
                                role="button"
                                aria-label={`${langInfo ? 'Hide' : 'Show'} language information`}
                                aria-expanded={langInfo}
                            /></div>
                            {langInfo ? <div className="lang-info">{parse(locales.lang_info)}</div> : ""}
                            <div> <i className="prof-icon timezone-icon"></i> {locales.timezone}: <b>{timezone}</b><button 
                                onClick={() => { setTimeZoneInfo(!timeZoneInfo); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setTimeZoneInfo(!timeZoneInfo);
                                    }
                                }}
                                className="info-icon"
                                role="button"
                                aria-label={`${timeZoneInfo ? 'Hide' : 'Show'} timezone information`}
                                aria-expanded={timeZoneInfo}
                            /></div>
                            {timeZoneInfo ? <div className="lang-info">{parse(locales.timezone_info)}</div> : ""}
                        </div>
                        <div>{getProductTiles()}</div>
                    </div>
                );
            } else {
                return (
                    <div className="text-container animate__animated animate__fadeIn">
                        <div className="pull-right action-btn">
                            <button 
                                className="btn" 
                                onClick={() => { historyAPI.push("/proactive-notifications/subscriptions/create") }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        historyAPI.push("/proactive-notifications/subscriptions/create");
                                    }
                                }}
                                aria-label={`${locales.create_subscriptions}, set up new subscription preferences`}
                                tabIndex={0}
                            >
                                {locales.create_subscriptions}
                            </button>
                        </div>
                        <div className="no-subscription">
                            <div>{locales.no_subscription}</div>
                            <p>{locales.no_subscription_messsage}</p>
                        </div>
                    </div>
                );
            }
        }
    }

    function renderTabContent() {
        return (currentTab === 'event' || window.location.href.indexOf("event") > -1) ? printEventInfo() : printProductInfo();
    }


    return (
        <div className="manage-subscription-container">
            <PNSHeader></PNSHeader>

            <div className="container subscription-container">
                <div className="subscription-tab">
                    <ul className="tabs" role="tablist" aria-label="Subscription management tabs">
                        <li
                            className={currentTab === 'product' ? `active` : ""}
                            onClick={() => switchTab('product')}
                            tabIndex={0}
                            role="tab"
                            aria-selected={currentTab === 'product'}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    switchTab('product');
                                }
                            }}
                        >
                            {locales.product_subscriptions}
                        </li>
                        <li
                            className={currentTab === 'event' ? `active` : ""}
                            onClick={() => switchTab('event')}
                            tabIndex={0}
                            role="tab"
                            aria-selected={currentTab === 'event'}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    switchTab('event');
                                }
                            }}
                        >
                            {locales.event_subscriptions}
                        </li>
                    </ul>
                    <div className="subscription-tab-content">
                        {error ? <div className="error-info">{parse(locales.api_error_overview)}</div> : renderTabContent()}
                    </div>
                </div>
            </div>
        </div>
    );
}